// src/components/ui/Logo.tsx (Simplified)
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Sparkles } from 'lucide-react';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  animated?: boolean;
  imageUrl?: string;
  showBadge?: boolean;
  variant?: 'default' | 'gradient' | 'simple';
}

export const Logo: React.FC<LogoProps> = ({ 
  size = 'md', 
  animated = true,
  imageUrl,
  showBadge = false,
  variant = 'default'
}) => {
  const sizes = {
    sm: {
      container: 'w-12 h-12',
      blur: 'w-16 h-16',
      icon: 'w-6 h-6'
    },
    md: {
      container: 'w-20 h-20',
      blur: 'w-24 h-24',
      icon: 'w-10 h-10'
    },
    lg: {
      container: 'w-24 h-24',
      blur: 'w-32 h-32',
      icon: 'w-12 h-12'
    },
    xl: {
      container: 'w-32 h-32',
      blur: 'w-40 h-40',
      icon: 'w-16 h-16'
    }
  };

  const gradients = {
    default: 'from-purple-500 to-pink-500',
    gradient: 'from-blue-500 via-purple-500 to-pink-500',
    simple: 'from-gray-600 to-gray-800'
  };

  const LogoContainer = animated ? motion.div : 'div';
  const AnimatedBlur = animated ? motion.div : 'div';
  const AnimatedLogo = animated ? motion.div : 'div';
  const AnimatedBadge = animated ? motion.div : 'div';

  return (
    <LogoContainer
      {...(animated && {
        initial: { scale: 0, rotate: -180 },
        animate: { scale: 1, rotate: 0 },
        transition: { delay: 0.2, type: "spring", bounce: 0.5, duration: 0.6 }
      })}
      className="flex justify-center mb-6 relative"
    >
      <div className="relative">
        <AnimatedBlur
          {...(animated && {
            animate: { scale: [1, 1.1, 1], opacity: [0.6, 0.8, 0.6] },
            transition: { duration: 3, repeat: Infinity, ease: "easeInOut" }
          })}
          className={`absolute -inset-2 ${sizes[size].blur} bg-gradient-to-r ${gradients[variant]} rounded-full blur-xl opacity-60`}
        />
        
        <AnimatedLogo
          {...(animated && {
            whileHover: { scale: 1.05 },
            transition: { duration: 0.3 }
          })}
          className={`
            relative 
            ${sizes[size].container} 
            bg-gradient-to-br ${gradients[variant]} 
            rounded-full 
            flex items-center justify-center 
            shadow-2xl
          `}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              className="w-full h-full rounded-full object-cover"
              alt="Logo"
            />
          ) : (
            <Shield className={`${sizes[size].icon} text-white`} />
          )}
        </AnimatedLogo>

        {showBadge && (
          <AnimatedBadge
            {...(animated && {
              initial: { scale: 0, opacity: 0 },
              animate: { scale: 1, opacity: 1 },
              transition: { delay: 0.4, type: "spring" }
            })}
            className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full p-1.5 shadow-lg"
          >
            <Sparkles className="w-3 h-3 text-white" />
          </AnimatedBadge>
        )}
      </div>
    </LogoContainer>
  );
};